const EFFECTIVE_DATE = "30 July 2026";
const EFFECTIVE_DATE_ISO = "2026-07-30";

export const legalDocuments = {
  disclaimer: {
    title: "Disclaimer",
    description: "The independence, informational-use, accuracy, copyright and external-source disclaimer for FU Research.",
    intro: "This disclaimer defines the purpose and boundaries of research published by FU Research.",
    path: "/legal/disclaimer",
    effectiveDate: EFFECTIVE_DATE,
    effectiveDateIso: EFFECTIVE_DATE_ISO,
    updatedDate: EFFECTIVE_DATE,
    updatedDateIso: EFFECTIVE_DATE_ISO,
    sections: [
      {
        id: "independence",
        title: "Independence",
        paragraphs: [
          "FU Research is an independent research platform. All opinions, analyses and conclusions published on this website are those of FU Research unless otherwise stated.",
          "Editorial judgements are made independently. References to companies, products, industries or institutions do not imply a commercial relationship or external approval of the research.",
        ],
      },
      {
        id: "no-affiliation",
        title: "No Affiliation",
        paragraphs: [
          "FU Research is not affiliated with, endorsed by, authorised by or sponsored by any company discussed on this website.",
          "Company names, trademarks and logos remain the property of their respective owners and are used solely for identification, commentary, criticism, education and research purposes.",
        ],
      },
      {
        id: "informational-purposes-only",
        title: "Informational Purposes Only",
        paragraphs: [
          "All content is provided solely for educational, informational and research purposes.",
          "Nothing published on this website constitutes financial advice, investment advice, legal advice, accounting advice or professional consulting services. The research does not take account of any reader’s individual objectives, financial circumstances or needs.",
          "Readers should conduct their own independent research and, where appropriate, obtain advice from a qualified professional before making any investment, legal, financial or business decision.",
        ],
      },
      {
        id: "accuracy",
        title: "Accuracy",
        paragraphs: [
          "Every effort is made to ensure information is accurate at the time of publication.",
          "However, FU Research makes no warranty regarding completeness, accuracy or future validity. Information may change after publication, and published materials may not reflect subsequent events, disclosures or developments.",
          "FU Research is under no obligation to revise or update previously published material, although corrections may be made where appropriate.",
        ],
      },
      {
        id: "copyright",
        title: "Copyright",
        paragraphs: [
          "Unless otherwise stated, all original written research, analysis and graphics created by FU Research are protected by copyright.",
          "Third-party trademarks and intellectual property remain the property of their respective owners. Limited quotation and reference may be permitted for criticism, review, scholarship or research where allowed by law and accompanied by appropriate attribution.",
        ],
      },
      {
        id: "external-sources",
        title: "External Sources",
        paragraphs: [
          "Research may reference annual reports, investor presentations, earnings calls, academic literature, public filings and other publicly available materials. These remain the intellectual property of their original owners.",
          "Links and citations are provided to identify sources and support independent verification. FU Research does not control, and is not responsible for, the availability, accuracy or practices of external sources.",
        ],
      },
    ],
  },

  privacy: {
    title: "Privacy Policy",
    description: "How FU Research handles browser-local reading preferences and limited technical delivery data across its independent research website.",
    intro: "This policy explains the limited information involved when readers visit FU Research or use its browser-local reading tools.",
    path: "/legal/privacy-policy",
    effectiveDate: EFFECTIVE_DATE,
    effectiveDateIso: EFFECTIVE_DATE_ISO,
    updatedDate: "23 August 2026",
    updatedDateIso: "2026-08-23",
    sections: [
      {
        id: "current-data-practices",
        title: "Current Data Practices",
        paragraphs: [
          "FU Research currently provides no reader accounts, public email-contact address, contact form, newsletter or email subscription.",
          "The website does not ask readers to submit names, email addresses, organisations, messages or attachments, and it does not create server-side saved-article or reading-progress histories.",
        ],
      },
      {
        id: "local-browser-storage",
        title: "Local Browser Storage",
        paragraphs: [
          "Save Research and reading-progress features use local browser storage. This information remains on the reader’s device, is not synchronised across devices and is not transmitted to FU Research.",
          "Readers can remove individual saved items through the website or clear local browser storage through their browser settings. Public research remains available when local storage is disabled or cleared.",
        ],
      },
      {
        id: "analytics",
        title: "Analytics and Tracking",
        paragraphs: [
          "FU Research does not embed third-party analytics, advertising trackers, session-recording tools or reader-profile services in the website.",
          "Reading progress and saved-research activity are not used to create server-side reader profiles. Following an external source link may, however, allow that destination to process information under its own privacy policy.",
        ],
      },
      {
        id: "technical-delivery-data",
        title: "Technical Delivery Data",
        paragraphs: [
          "Website hosting and security infrastructure may process limited request information, such as an IP address, browser type and timestamp, where necessary to deliver the public website, maintain availability and protect against misuse.",
          "FU Research does not use technical delivery data to build reader profiles, reconstruct reading histories or synchronise saved articles across devices.",
        ],
      },
      {
        id: "third-party-services",
        title: "Third-Party Services",
        paragraphs: [
          "FU Research may rely on service providers for website hosting, content delivery and security. These providers may process limited information needed to perform those functions and may operate in jurisdictions outside the reader’s country.",
          "Third-party websites, embedded media and external links are governed by their own privacy policies. FU Research does not control those services and encourages readers to review the relevant terms before providing information.",
        ],
      },
      {
        id: "data-security",
        title: "Data Security and Retention",
        paragraphs: [
          "FU Research uses reasonable administrative and technical measures designed to protect personal information from unauthorised access, alteration, disclosure or destruction. No internet transmission or storage system can, however, be guaranteed to be completely secure.",
          "Browser-local saved articles and reading progress remain under the reader’s control and can be removed through the website or browser settings. Technical delivery information, where processed by infrastructure providers, is subject to applicable operational, security and legal retention requirements.",
        ],
      },
      {
        id: "user-rights",
        title: "User Rights",
        paragraphs: [
          "Depending on the reader’s location, applicable law may provide rights in relation to personal information. FU Research will consider valid requests in accordance with the law that applies.",
        ],
        bullets: [
          "Request access to, or a copy of, personal information held about you.",
          "Request correction, deletion or restriction of personal information.",
          "Object to certain processing or withdraw consent where processing relies on consent.",
          "Request data portability where applicable.",
          "Lodge a complaint with an appropriate privacy or data-protection authority.",
        ],
      },
      {
        id: "children",
        title: "Children’s Privacy",
        paragraphs: [
          "The website is intended for a general business and research audience and is not directed to children under 16. FU Research does not knowingly collect personal information from children. If such information is identified, reasonable steps will be taken to delete it.",
        ],
      },
      {
        id: "policy-changes",
        title: "Changes to This Policy",
        paragraphs: [
          "This policy may be updated to reflect changes in the website, operational practices or applicable law. The revised version will be published on this page with an updated date. Material changes may also be communicated by an appropriate notice on the website.",
        ],
      },
    ],
  },

  terms: {
    title: "Terms of Use",
    description: "The terms governing access to and use of the FU Research website, publications, intellectual property and external materials.",
    intro: "These terms govern access to FU Research and the responsible use of its independent research and publications.",
    path: "/legal/terms-of-use",
    effectiveDate: EFFECTIVE_DATE,
    effectiveDateIso: EFFECTIVE_DATE_ISO,
    updatedDate: EFFECTIVE_DATE,
    updatedDateIso: EFFECTIVE_DATE_ISO,
    sections: [
      {
        id: "acceptance-of-terms",
        title: "Acceptance of Terms",
        paragraphs: [
          "By accessing or using this website, you agree to these Terms of Use and the accompanying Disclaimer and Privacy Policy. If you do not agree, you should not use the website.",
          "These terms apply to all visitors, readers and users of FU Research. Additional terms may apply to future services and will be presented before those services are used.",
        ],
      },
      {
        id: "permitted-use",
        title: "Permitted Use",
        paragraphs: [
          "The website may be used for lawful personal, educational, journalistic and internal business research purposes. Readers may link to FU Research and may quote limited extracts where permitted by law, provided the source and relevant publication are clearly attributed.",
          "Search engines and AI services may crawl and index publicly available pages in accordance with the published robots.txt and applicable law. Permission to index does not grant a right to reproduce or commercially republish substantial portions of the website.",
        ],
        bullets: [
          "Do not copy, republish, sell or commercially exploit substantial research without prior written permission.",
          "Do not interfere with the security, integrity, availability or normal operation of the website.",
          "Do not use the website for unlawful, deceptive, abusive or rights-infringing activity.",
          "Do not misrepresent FU Research content, authorship, endorsement or affiliation.",
        ],
      },
      {
        id: "intellectual-property",
        title: "Intellectual Property",
        paragraphs: [
          "Unless otherwise stated, the website’s original text, research, analysis, visual presentation, graphics and branding are owned by or licensed to FU Research and are protected by applicable intellectual-property laws.",
          "Company names, logos, trademarks, images and source materials belonging to third parties remain the property of their respective owners. Their appearance on the website does not transfer any ownership or licence to the reader.",
        ],
      },
      {
        id: "user-responsibilities",
        title: "User Responsibilities",
        paragraphs: [
          "Users are responsible for evaluating the relevance, accuracy and suitability of all information before relying on it. Users must comply with applicable laws and respect the rights of FU Research and third parties when quoting, sharing or otherwise using website material.",
          "Users must not attempt to gain unauthorised access, introduce malicious code, overload the website, evade technical controls or collect personal information about other users.",
        ],
      },
      {
        id: "third-party-links",
        title: "Third-Party Links",
        paragraphs: [
          "The website may link to company reports, regulatory filings, academic publications, media, service providers and other third-party resources. Links are provided for source identification, context and convenience.",
          "FU Research does not control or endorse third-party websites and is not responsible for their content, availability, security, accuracy or privacy practices. Accessing an external resource is at the reader’s own discretion.",
        ],
      },
      {
        id: "no-professional-advice",
        title: "No Professional Advice",
        paragraphs: [
          "FU Research provides independent commentary and research, not financial, investment, legal, accounting, tax or professional consulting advice. Nothing on the website is an offer, recommendation or solicitation to buy, sell or hold any security, product or service.",
          "Readers should undertake their own analysis and seek appropriately qualified advice before making decisions that may carry financial, legal or commercial consequences.",
        ],
      },
      {
        id: "limitation-of-liability",
        title: "Limitation of Liability",
        paragraphs: [
          "To the maximum extent permitted by law, FU Research excludes all warranties not expressly stated and will not be liable for any loss, damage, cost or expense arising from access to, use of, inability to use, or reliance on the website or any linked material, including indirect, incidental, consequential or economic loss.",
          "Nothing in these terms excludes, restricts or modifies a guarantee, right or remedy that cannot lawfully be excluded, including any applicable rights under the Australian Consumer Law. Where liability cannot be excluded, it is limited to the minimum extent permitted by law.",
        ],
      },
      {
        id: "changes-to-the-website",
        title: "Changes to the Website",
        paragraphs: [
          "FU Research may add, update, correct, suspend or remove website content or functionality at any time. There is no obligation to maintain any publication indefinitely or to provide advance notice of editorial, technical or availability changes.",
          "These terms may also be revised. Continued use of the website after revised terms are published constitutes acceptance of the updated terms to the extent permitted by law.",
        ],
      },
      {
        id: "governing-law",
        title: "Governing Law",
        paragraphs: [
          "These Terms of Use are governed by the laws of New South Wales, Australia, without regard to conflict-of-law principles. Subject to any mandatory rights that apply in another jurisdiction, the courts of New South Wales and the courts entitled to hear appeals from them will have non-exclusive jurisdiction over disputes relating to these terms or the website.",
        ],
      },
    ],
  },
};